import { Language, fromApiLocale, getLocale, toApiLocale } from '@binhatch/locale';
import flat from 'flat';
import React, { StrictMode } from 'react';
import { Root, createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';
import 'tailwindcss/tailwind.css';

import { notificationApi } from '@/integrations/api';

import { Auth } from '@/containers/useAuth';
import { Configuration } from '@/containers/useConfiguration';
import { Tenant } from '@/containers/useTenant';

import { AnalyticsTracker } from '@/components/AnalyticsTracker';

import { LoadingPage } from '@/pages/LoadingPage';
import { EmbeddedPromotionListPage } from '@/pages/promotions/EmbeddedPromotionListPage';

const root = createRoot(document.getElementById('app')!);

const AppWithAuth: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { authenticated } = Auth.useContainer();

  if (!authenticated) return <LoadingPage />;

  return <React.Fragment>{children}</React.Fragment>;
};

const AppWithTenant: React.FC<React.PropsWithChildren> = ({ children }) => {
  const tenant = Tenant.useContainer();

  if (!tenant.data) return <LoadingPage />;

  return (
    <Language.Provider
      initialState={{
        locale: getLocale(fromApiLocale(tenant.data.locale)),
        onChange: (locale) => notificationApi.updatePreferences({ preferredLocale: toApiLocale(locale) })
      }}
    >
      <AppWithLanguage>{children}</AppWithLanguage>
    </Language.Provider>
  );
};

const AppWithLanguage: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [locale, , messages] = Language.useContainer();

  if (!messages.data) return <LoadingPage />;

  return (
    <IntlProvider defaultLocale={locale} defaultRichTextElements={{ br: (<br />) as any }} messages={flat(messages.data)} {...{ locale }}>
      {children}
    </IntlProvider>
  );
};

function bootstrapApplication(element: Root) {
  element.render(
    <StrictMode>
      <BrowserRouter>
        <Tenant.Provider>
          <AppWithTenant>
            <Auth.Provider>
              <AppWithAuth>
                <Configuration.Provider>
                  <AnalyticsTracker />
                  <div className="flex flex-col p-4">
                    <EmbeddedPromotionListPage />
                  </div>
                </Configuration.Provider>
              </AppWithAuth>
            </Auth.Provider>
          </AppWithTenant>
        </Tenant.Provider>
      </BrowserRouter>
    </StrictMode>
  );
}

bootstrapApplication(root);
